import api from '../../../services/api';
import type { TeamMember, Team, AgentMember } from '../../../types';

export interface RecurringTask {
  id: string;
  title: string;
  description: string | null;
  acceptance: string | null;
  weekdays: number[];
  hour: number;
  enabled: boolean;
}
export interface RecurringInput {
  title: string;
  description?: string;
  acceptance?: string;
  weekdays: number[];
  hour: number;
}

export const teamService = {
  // 获取团队成员列表
  async getTeamMembers(teamId: string): Promise<TeamMember[]> {
    const response = await api.get(`/teams/${teamId}/members`);
    return response.data;
  },

  // 移除团队成员
  async removeMember(teamId: string, userId: string): Promise<void> {
    await api.delete(`/teams/${teamId}/members/${userId}`);
  },

  // 更新成员角色
  async updateMemberRole(teamId: string, userId: string, role: string): Promise<TeamMember> {
    const response = await api.patch(`/teams/${teamId}/members/${userId}/role`, { role });
    return response.data;
  },

  // 获取用户所有团队
  async getMyTeams(): Promise<Team[]> {
    const response = await api.get('/teams');
    return response.data;
  },

  // 获取团队详细信息
  async getTeamInfo(teamId: string): Promise<Team> {
    const response = await api.get(`/teams/${teamId}`);
    return response.data;
  },

  // 加入团队
  async joinTeam(inviteCode: string): Promise<Team> {
    const response = await api.post('/teams/join', { inviteCode });
    return response.data;
  },

  // 创建团队
  async createTeam(name: string, description?: string): Promise<Team> {
    const response = await api.post('/teams', { name, description });
    return response.data;
  },

  // 重新生成邀请码
  async regenerateInviteCode(teamId: string): Promise<string> {
    const response = await api.post(`/teams/${teamId}/regenerate-invite`);
    return response.data.inviteCode;
  },

  // 定期任务（团队所有者/管理员）
  async getRecurring(teamId: string): Promise<RecurringTask[]> {
    return (await api.get(`/teams/${teamId}/recurring`)).data;
  },
  async createRecurring(teamId: string, data: RecurringInput): Promise<RecurringTask> {
    return (await api.post(`/teams/${teamId}/recurring`, data)).data;
  },
  async updateRecurring(teamId: string, id: string, data: Partial<RecurringInput> & { enabled?: boolean }): Promise<RecurringTask> {
    return (await api.patch(`/teams/${teamId}/recurring/${id}`, data)).data;
  },
  async deleteRecurring(teamId: string, id: string): Promise<void> {
    await api.delete(`/teams/${teamId}/recurring/${id}`);
  },

  // 通知设置（团队所有者/管理员）
  async getNotify(teamId: string): Promise<{ configured: boolean; masked: string | null }> {
    return (await api.get(`/teams/${teamId}/notify`)).data;
  },
  async setNotify(teamId: string, webhook: string | null): Promise<{ configured: boolean; masked: string | null }> {
    return (await api.patch(`/teams/${teamId}/notify`, { webhook })).data;
  },
  async testNotify(teamId: string): Promise<void> {
    await api.post(`/teams/${teamId}/notify/test`);
  },

  // agent 成员管理（团队所有者/管理员）
  async getAgents(teamId: string): Promise<AgentMember[]> {
    const response = await api.get(`/teams/${teamId}/agents`);
    return response.data;
  },

  async createAgent(teamId: string, name: string): Promise<{ agent: AgentMember; token: { token: string } }> {
    const response = await api.post(`/teams/${teamId}/agents`, { name });
    return response.data;
  },

  async createAgentToken(teamId: string, agentId: string, name: string): Promise<{ token: string }> {
    const response = await api.post(`/teams/${teamId}/agents/${agentId}/tokens`, { name });
    return response.data;
  },

  async revokeAgentToken(teamId: string, agentId: string, tokenId: string): Promise<void> {
    await api.delete(`/teams/${teamId}/agents/${agentId}/tokens/${tokenId}`);
  },

  // 切换当前团队
  async switchTeam(teamId: string): Promise<void> {
    // 这里可以调用API来更新用户的当前团队
    // 简单实现：存储到localStorage并刷新页面
    localStorage.setItem('currentTeamId', teamId);
    window.location.reload();
  }
};